import React, { useState } from 'react';
import { Package, DollarSign, AlertTriangle, CheckCircle, XCircle, ChevronDown, ChevronRight } from 'lucide-react';
import { Project, forecastService } from '../../lib/forecastService';
import { useToast } from '../../contexts/ToastContext';
import MilestoneManager from './MilestoneManager';

interface ProjectListProps {
  projects: Project[];
  onRefresh: () => void;
}

const ProjectList: React.FC<ProjectListProps> = ({ projects, onRefresh }) => {
  const { showToast } = useToast();
  const [expandedProject, setExpandedProject] = useState<string | null>(null);

  const handleAbandonProject = async (project: Project) => {
    const notes = prompt('Why was this project abandoned?');
    if (notes === null) return;

    try {
      await forecastService.abandonProject(project.id, null, notes);
      showToast('Project marked as abandoned', 'success');
      onRefresh();
    } catch (error) {
      showToast('Failed to abandon project', 'error');
      console.error('Error abandoning project:', error);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <Package className="w-4 h-4 text-blue-600" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'abandoned':
        return <XCircle className="w-4 h-4 text-red-600" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'abandoned':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (projects.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
        <Package className="w-12 h-12 text-gray-400 mx-auto mb-3" />
        <p className="text-gray-600 font-medium">No projects</p>
        <p className="text-gray-500 text-sm mt-1">Add a project to track milestone-based revenue</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {projects.map((project) => {
        const isExpanded = expandedProject === project.id;
        const completionPercentage = project.total_value > 0
          ? (project.total_paid / project.total_value) * 100
          : 0;

        return (
          <div
            key={project.id}
            className="bg-white rounded-lg border border-gray-200 overflow-hidden"
          >
            <div className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setExpandedProject(isExpanded ? null : project.id)}
                      className="p-1 hover:bg-gray-100 rounded transition-colors"
                    >
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-gray-600" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-gray-600" />
                      )}
                    </button>
                    <h4 className="font-semibold text-gray-900">{project.project_name}</h4>
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(project.status)}`}>
                      {getStatusIcon(project.status)}
                      {project.status.charAt(0).toUpperCase() + project.status.slice(1)}
                    </span>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-4">
                    <div className="flex items-center gap-2 text-sm">
                      <DollarSign className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-600">
                        Total: ${project.total_value.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <DollarSign className="w-4 h-4 text-green-600" />
                      <span className="text-gray-600">
                        Paid: ${project.total_paid.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                  <div className="mt-3">
                    <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                      <span>Progress</span>
                      <span>{completionPercentage.toFixed(0)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all"
                        style={{ width: `${completionPercentage}%` }}
                      />
                    </div>
                  </div>
                  {project.abandonment_notes && (
                    <div className="mt-2 flex items-start gap-2 text-sm">
                      <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-600">{project.abandonment_notes}</span>
                    </div>
                  )}
                </div>
                {project.status === 'active' && (
                  <button
                    onClick={() => handleAbandonProject(project)}
                    className="ml-4 px-3 py-1 text-sm text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                  >
                    Abandon
                  </button>
                )}
              </div>
            </div>

            {isExpanded && (
              <div className="border-t border-gray-200 bg-gray-50 p-4">
                <MilestoneManager projectId={project.id} onUpdate={onRefresh} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ProjectList;